import React, { useCallback } from 'react';
import { TopicCardRecord } from '../../Topics/types';
import ElementCard from 'src/components/BeaconV2Components/ElementCard/ElementCard';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { Link } from 'react-router-dom';
import _capitalize from 'lodash/capitalize';
import { SlFlippableCard } from 'src/components/BeaconV2Components/SlFlippableCard';
import SplineChart from 'src/components/BeaconV2Components/SplineChart/SplineChart';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import {
  getColorForMetricChangeValue,
  getIconForMetricChange
} from 'src/components/BeaconV2Components/utils/chartStyles';
import { SlCardBack, SlCardFooter, SlCardTitle } from 'src/components/BeaconV2Components/SlCard';
import MiniButton from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/MiniButton';
import { hexToRgba, useStacklineTheme } from '@stackline/ui';
import TopicOwner from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/TopicOwner';
import { useMainEntity } from 'src/utils/Hooks/reduxSelectors';
import { useRemoveTopicModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/hooks/useRemoveTopicModal';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { useAddTopicGroupStoreActions } from 'src/zustandStores/BeaconPro/Topics/TopicGroups/useAddTopicGroupStore';

interface TopicGroupCardProps {
  topicGroup: TopicCardRecord;
}

/**
 * Displays a topic group with its weekly traffic data
 */
export function TopicGroupCard({ topicGroup }: TopicGroupCardProps) {
  const formatMetric = useMetricFormatter();
  const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);
  const theme = useStacklineTheme();
  const { beaconNavigator } = useBeaconNavigator();
  const { openModal: openAddTopicGroupModal, setTopicGroupState } = useAddTopicGroupStoreActions();

  const topicEntity = useMainEntity();

  const { openModal: openRemoveTopicModal } = useRemoveTopicModal();

  const sx = {
    backgroundColor: hexToRgba(theme.colors.accentPlum, 0.1),
    '&:hover': {
      backgroundColor: hexToRgba(theme.colors.accentPlum, 0.1)
    }
  };

  const handleRemove = useCallback(() => {
    openRemoveTopicModal({
      topicId: topicEntity.id,
      topicGroupName: topicGroup.name,
      topicGroupId: `${topicGroup.id}`
    });
  }, [openRemoveTopicModal, topicEntity.id, topicGroup.id, topicGroup.name]);

  const handleEdit = useCallback(() => {
    beaconNavigator.setPathname(`/topicgroup/${topicGroup.id}`);
    beaconNavigator.addSearchParams({ tab: ['traffic'], subtab: ['settings'] });
    beaconNavigator.go();
  }, [beaconNavigator, topicGroup.id]);

  const handleDuplicate = useCallback(() => {
    setTopicGroupState(({ setCategoryIds }) => setCategoryIds(topicGroup?.topicMetadata?.includedCategoryIds ?? []));
    setTopicGroupState(({ setSubcategoryIds }) =>
      setSubcategoryIds(topicGroup?.topicMetadata?.includedSubCategoryIds ?? [])
    );
    setTopicGroupState(({ setTopicIds }) => setTopicIds(topicGroup?.topicMetadata?.topicIds ?? []));
    openAddTopicGroupModal();
  }, [openAddTopicGroupModal, setTopicGroupState, topicGroup]);

  const buttonColor = 'accentPlum';

  const renderButtons = useCallback(() => {
    switch (topicEntity?.ownership) {
      case 'Owned':
        switch (topicGroup?.ownership) {
          case 'Owned':
            return (
              <Flex marginBottom="24px" gap={8}>
                <MiniButton onClick={handleEdit} sx={sx}>
                  <Text color={buttonColor}>Edit</Text>
                </MiniButton>
                <MiniButton onClick={handleRemove} sx={sx}>
                  <Text color={buttonColor}>Remove</Text>
                </MiniButton>
                <MiniButton onClick={handleDuplicate} sx={sx}>
                  <Text color={buttonColor}>Duplicate</Text>
                </MiniButton>
              </Flex>
            );

          case 'Custom':
            return (
              <Flex marginBottom="24px" gap={8}>
                <MiniButton onClick={handleRemove} sx={sx}>
                  <Text color={buttonColor}>Remove</Text>
                </MiniButton>
                <MiniButton onClick={handleDuplicate} sx={sx}>
                  <Text color={buttonColor}>Duplicate</Text>
                </MiniButton>
              </Flex>
            );

          default:
            return null;
        }
      case 'Custom':
        switch (topicGroup?.ownership) {
          case 'Owned':
            return (
              <Flex marginBottom="24px" gap={8}>
                <MiniButton onClick={handleEdit} sx={sx}>
                  <Text color={buttonColor}>Edit</Text>
                </MiniButton>
                <MiniButton onClick={handleDuplicate} sx={sx}>
                  <Text color={buttonColor}>Duplicate</Text>
                </MiniButton>
              </Flex>
            );

          case 'Custom':
            return (
              <Flex marginBottom="24px" gap={8}>
                <MiniButton onClick={handleDuplicate} sx={sx}>
                  <Text color={buttonColor}>Duplicate</Text>
                </MiniButton>
              </Flex>
            );

          default:
            return null;
        }
      default:
        return null;
    }
  }, [handleRemove, handleEdit, handleDuplicate, topicGroup, topicEntity, sx]);

  return (
    <SlFlippableCard
      flipIconSize="16px"
      sx={{
        height: '416px',
        width: '250px'
      }}
      cardFront={() => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          <div
            style={{
              paddingTop: '16px',
              paddingLeft: '16px',
              paddingRight: '16px',
              marginBottom: '92px'
            }}
          >
            <ElementCard textSx={{ fontWeight: 700 }} color="accentPlum" variant="extraSmall">
              {_capitalize(topicGroup.name.slice(0, 2))}
            </ElementCard>
            <SlCardTitle sx={{ marginTop: '16px', fontSize: '16px', fontWeight: 500 }}>
              <Link to={`/topicgroup/${topicGroup.id}${searchParams}${additionalParams}`}>{topicGroup.name}</Link>
            </SlCardTitle>
          </div>
          <SplineChart
            preview={{
              width: 244,
              height: 64
            }}
            removeSpacing
            metricType={METRICTYPE.VOLUME}
            primaryData={topicGroup.splineData}
            secondaryData={topicGroup.comparisonSplineData}
            chartOptionsOverride={{
              plotOptions: {
                series: {
                  lineWidth: 2
                }
              }
            }}
          />
          <div style={{ flex: 1 }}></div>
          <Flex
            flexDirection="column"
            gap={4}
            sx={{
              padding: '0px 16px 16px'
            }}
          >
            <Text
              transform="uppercase"
              sx={{
                fontSize: '12px',
                fontWeight: 'bold',
                height: '20px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              Total Traffic
            </Text>
            <Flex gap="sm" alignItems="center">
              <Text variant="body2">
                {formatMetric(topicGroup.totalTraffic, METRICTYPE.VOLUME, { decimalPlaces: 2, showFullValue: false })}
              </Text>
              <Flex gap="xs" alignItems="center">
                <div
                  style={{
                    transform: 'translateY(1px)'
                  }}
                >
                  {getIconForMetricChange(topicGroup.totalTrafficPercentChange)}
                </div>
                <Text variant="body4" color={getColorForMetricChangeValue(topicGroup.totalTrafficPercentChange)}>
                  {`${formatMetric(Math.abs(topicGroup.totalTrafficPercentChange), METRICTYPE.PERCENT, {
                    showFullValue: false
                  })} YTD`}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </div>
      )}
      cardBack={() => (
        <SlCardBack
          sx={{
            paddingTop: '20px',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingBottom: '16px'
          }}
        >
          {renderButtons()}
          <Text
            variant="subtitle4"
            transform="uppercase"
            sx={{
              marginBottom: '4px'
            }}
          >
            Topic Group Type
          </Text>
          <Text variant="body4">{topicGroup.ownership}</Text>

          <SlCardFooter sx={{ padding: 0 }}>
            <TopicOwner
              createdAt={topicGroup?.topicMetadata?.createdAt}
              firstName={topicGroup?.owner?.firstName}
              lastName={topicGroup?.owner?.lastName}
              userId={topicGroup?.topicMetadata?.ownedByUserId}
            />
          </SlCardFooter>
        </SlCardBack>
      )}
    />
  );
}
